import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BUILD_INFO } from '../buildInfo';
import Lottie from 'lottie-react';
import kangarooAnimation from '../assets/kangaroo.json';

import { getSmsPricing } from '../lib/sms-pricing';

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [smsCount, setSmsCount] = useState(100);
  const [contactForm, setContactForm] = useState({ businessName: '', contactName: '', phone: '', time: '' });
  const [contactSubmitted, setContactSubmitted] = useState(false);

  const pricePerSms = getSmsPricing(smsCount);
  const totalCost = (smsCount * pricePerSms).toFixed(2);
  const adCostComparison = (smsCount * 3).toLocaleString();

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="#f97316" viewBox="0 0 256 256">
              <path d="M169.57,72.59A80,80,0,0,0,16,104v64a16,16,0,0,0,16,16H86.67A80.15,80.15,0,0,0,160,232h64a16,16,0,0,0,16-16V152A80,80,0,0,0,169.57,72.59ZM32,104a64,64,0,1,1,64,64H32ZM224,216H160a64.14,64.14,0,0,1-55.68-32.43A79.93,79.93,0,0,0,174.7,89.71,64,64,0,0,1,224,152Z"></path>
            </svg>
            <div>
              <div className="text-xl font-bold text-gray-900">DAZZA</div>
              <div className="text-xs text-gray-500">dazza.ai</div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <a href="#pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
              Pricing
            </a>
            <a href="#how-it-works" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
              How It Works
            </a>
            <a href="#contact" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
              Contact Us
            </a>
            <Link to="/sign-in" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-4 py-2">
              Log in
            </Link>
            <Link to="/sign-up" className="btn-primary text-sm">
              Get $5 Free Credit
            </Link>
          </div>

          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-gray-900" 
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
        </div>

        <div className={`md:hidden border-t border-gray-100 bg-white ${mobileMenuOpen ? '' : 'hidden'}`} data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-3">
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-base font-medium text-gray-600 hover:text-gray-900">Pricing</a>
            <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-base font-medium text-gray-600 hover:text-gray-900">How It Works</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-base font-medium text-gray-600 hover:text-gray-900">Contact Us</a>
            <Link to="/sign-in" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-base font-medium text-gray-600 hover:text-gray-900">Log in</Link>
            <Link to="/sign-up" onClick={() => setMobileMenuOpen(false)} className="btn-primary w-full text-center block">Get $5 Free Credit</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-amber-50"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-200 rounded-full blur-3xl opacity-30"></div>
        
        <div className="relative mx-auto max-w-6xl px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-sm font-medium">
                <span className="text-lg">🇦🇺</span>
                Built for Australian businesses
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Turn SMS into{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                  real orders
                </span>
                {' '}— not ad spend guesses
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Send SMS to your customers in seconds, track exactly who converts, and only pay for what you send.
                <span className="block mt-2 font-medium text-gray-900">No subscriptions. No limits. No wasted spend.</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/sign-up" className="btn-primary text-lg px-8 py-4 text-center" data-testid="hero-get-started-btn">
                  Get $5 Free SMS Credit
                </Link>
                <a href="#how-it-works" className="btn-outline text-lg px-8 py-4 text-center">
                  See How It Works
                </a>
              </div>
            </div>
            
            {/* Phone Mockup */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-amber-400 rounded-3xl blur-2xl opacity-20 transform rotate-6"></div>
              <div className="relative bg-gray-800 rounded-[3rem] p-3 shadow-2xl">
                <div className="absolute top-6 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-800 rounded-full z-10"></div>
                <div className="bg-white rounded-[2.5rem] overflow-hidden">
                  <div className="bg-gray-100 px-6 pt-8 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#ffffff" viewBox="0 0 256 256">
                          <path d="M169.57,72.59A80,80,0,0,0,16,104v64a16,16,0,0,0,16,16H86.67A80.15,80.15,0,0,0,160,232h64a16,16,0,0,0,16-16V152A80,80,0,0,0,169.57,72.59ZM32,104a64,64,0,1,1,64,64H32ZM224,216H160a64.14,64.14,0,0,1-55.68-32.43A79.93,79.93,0,0,0,174.7,89.71,64,64,0,0,1,224,152Z"></path>
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Johnny Boys Pizza</div>
                        <div className="text-xs text-gray-500">SMS Campaign</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 min-h-[280px] bg-white">
                    <div className="text-center text-xs text-gray-400 mb-4">Today</div>
                    <div className="space-y-3">
                      <div className="flex justify-start">
                        <div className="bg-gray-100 text-gray-900 rounded-2xl rounded-tl-md px-4 py-3 text-sm max-w-[85%]">
                          Hey! 20% off all pizzas this weekend. Use code WEEKEND20 at checkout. Order now: johnnyboys.pizza
                        </div>
                      </div>
                      <div className="flex justify-start">
                        <div className="bg-gray-100 text-gray-900 rounded-2xl rounded-tl-md px-4 py-3 text-sm max-w-[85%]">
                          Reply STOP to opt out.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-gray-50 border-y border-gray-100 py-6">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span>Trusted by Australian businesses</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span>No contracts • No lock-in • Unused credit never expires</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span>Track real orders, not clicks</span>
            </div>
          </div>
        </div>
      </section>

      {/* Kangaroo Animation */}
      <section className="relative overflow-hidden bg-white py-6">
        <div className="w-full">
          <div className="relative h-32">
            <div 
              className="absolute h-32 w-32"
              style={{
                animation: 'kangarooHop 8s linear infinite',
                right: '-150px',
              }}
            >
              <Lottie 
                animationData={kangarooAnimation} 
                loop={true}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </div>
        <style>{`
          @keyframes kangarooHop {
            0% { right: -150px; }
            100% { right: calc(100% + 150px); }
          }
        `}</style>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Paid ads are a black box. SMS isn&apos;t.
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Google and Facebook ads charge you before you know if they worked.
            You spend money, hope for results, and guess the return.
          </p>
          <div className="bg-gray-800 rounded-2xl p-8 text-left space-y-4">
            <h3 className="text-lg font-semibold text-orange-400 mb-4">With DAZZA:</h3>
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span className="text-gray-200">You message customers who already know your business</span>
            </div>
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span className="text-gray-200">You see exactly which campaigns lead to real orders</span>
            </div>
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span className="text-gray-200">If no one converts, you don&apos;t pretend it worked</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How DAZZA Works
            </h2>
            <p className="text-xl text-gray-600">Simple, honest, measurable.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gray-50 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">💳</span>
              </div>
              <div className="text-sm font-bold text-orange-600 mb-2">Step 1</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Add Wallet Credit</h3>
              <p className="text-gray-600">
                Top up your wallet when you&apos;re ready. Unused balance stays forever.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gray-50 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">📱</span>
              </div>
              <div className="text-sm font-bold text-orange-600 mb-2">Step 2</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Send SMS Campaigns</h3>
              <p className="text-gray-600">
                Send promotions, reminders, updates, or announcements in seconds.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gray-50 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">📊</span>
              </div>
              <div className="text-sm font-bold text-orange-600 mb-2">Step 3</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Track Real Revenue</h3>
              <p className="text-gray-600">
                When customers use your promo code to purchase, DAZZA tracks it automatically.
              </p>
              <p className="text-sm text-gray-500 mt-3 italic">
                No promo code = no attribution. That&apos;s intentional — we only count what&apos;s provable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wallet Explanation Section */}
      <section className="py-20 bg-orange-50">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Pay for SMS like airtime — not like a subscription
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            There are no plans, no monthly limits, and no forced upgrades.
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl mb-3">🎁</div>
              <p className="font-medium text-gray-900">Start with $5 free credit</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl mb-3">💰</div>
              <p className="font-medium text-gray-900">Top up when needed</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl mb-3">📤</div>
              <p className="font-medium text-gray-900">Each SMS deducts from balance</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl mb-3">📉</div>
              <p className="font-medium text-gray-900">More you send = cheaper per SMS</p>
            </div>
          </div>
          
          <p className="text-lg text-gray-700 font-medium">That&apos;s it.</p>
          
          <a href="#pricing" className="inline-block mt-6 text-orange-600 font-semibold hover:text-orange-700">
            View SMS Pricing →
          </a>
        </div>
      </section>

      {/* SMS Pricing Slider Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How many SMS do you want to send?
            </h2>
            <p className="text-gray-600">Slide to see your cost. All prices in AUD, ex GST.</p>
          </div>
          
          <div className="bg-gray-50 rounded-2xl p-8 lg:p-12">
            {/* Slider */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-gray-500 mb-2">
                <span>100</span>
                <span>1,000</span>
                <span>10,000</span>
                <span>50,000</span>
                <span>100,000+</span>
              </div>
              <input
                type="range"
                min="100"
                max="100000"
                step="100"
                value={smsCount}
                onChange={(e) => setSmsCount(Number(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
              />
            </div>
            
            {/* Results */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="text-sm text-gray-500 mb-1">SMS Count</div>
                <div className="text-3xl font-bold text-gray-900">{smsCount.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="text-sm text-gray-500 mb-1">Price per SMS</div>
                <div className="text-3xl font-bold text-orange-600">${pricePerSms.toFixed(3)}</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-sm border-2 border-orange-200">
                <div className="text-sm text-gray-500 mb-1">Total Cost</div>
                <div className="text-3xl font-bold text-gray-900">${totalCost}</div>
              </div>
            </div>
            
            {/* Pricing Tiers */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              <div className={`p-4 rounded-lg text-center ${smsCount <= 1000 ? 'bg-orange-100 border-2 border-orange-400' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm text-gray-600">Up to 1,000</div>
                <div className="font-bold text-gray-900">$0.065/SMS</div>
              </div>
              <div className={`p-4 rounded-lg text-center ${smsCount > 1000 && smsCount <= 10000 ? 'bg-orange-100 border-2 border-orange-400' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm text-gray-600">Up to 10,000</div>
                <div className="font-bold text-gray-900">$0.055/SMS</div>
              </div>
              <div className={`p-4 rounded-lg text-center ${smsCount > 10000 && smsCount <= 50000 ? 'bg-orange-100 border-2 border-orange-400' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm text-gray-600">Up to 50,000</div>
                <div className="font-bold text-gray-900">$0.045/SMS</div>
              </div>
              <div className={`p-4 rounded-lg text-center ${smsCount > 50000 ? 'bg-orange-100 border-2 border-orange-400' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm text-gray-600">50,000+</div>
                <div className="font-bold text-gray-900">$0.038/SMS</div>
              </div>
            </div>
            
            {/* Comparison */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <p className="text-emerald-800">
                <span className="font-semibold">Reaching {smsCount.toLocaleString()} customers via ads would cost ~${adCostComparison}.</span>
                <span className="block text-sm mt-1">Your SMS cost: ${totalCost}</span>
              </p>
            </div>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              SMS reaches people who already trust your business.
            </p>
            
            {/* Custom Sender ID Note */}
            <div className="mt-6 p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                <strong>Custom Sender ID:</strong> Want your business name instead of a number? Setup fee of $299 + GST.
              </p>
            </div>
            
            {/* Minimum Note */}
            <p className="text-center text-xs text-gray-400 mt-4">
              Minimum wallet top-up: $50. All prices ex GST.
            </p>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Measure sales — not clicks or impressions
            </h2>
            <p className="text-xl text-gray-300">
              DAZZA only counts revenue when a real transaction occurs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="relative">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="#f97316" viewBox="0 0 256 256"><path d="M164.49,163.51a12,12,0,1,1-17,0A12,12,0,0,1,164.49,163.51Zm-81-8a12,12,0,1,0,17,0A12,12,0,0,0,83.51,155.51Zm9-39a12,12,0,1,0-17,0A12,12,0,0,0,92.49,116.49Zm48-1a12,12,0,1,0,0,17A12,12,0,0,0,140.49,115.51ZM232,128A104,104,0,1,1,128,24a8,8,0,0,1,8,8,40,40,0,0,0,40,40,8,8,0,0,1,8,8,40,40,0,0,0,40,40A8,8,0,0,1,232,128Zm-16.31,7.39A56.13,56.13,0,0,1,168.5,87.5a56.13,56.13,0,0,1-47.89-47.19,88,88,0,1,0,95.08,95.08Z"></path></svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-0.5 bg-red-500 rotate-45"></div>
                  </div>
                </div>
              </div>
              <p className="text-gray-300">No cookies</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="relative">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="#f97316" viewBox="0 0 256 256"><path d="M240,149.31c0,16.11-3.17,29.89-9.17,39.84-7.43,12.33-19,18.85-33.39,18.85-27.94,0-47.78-37-68.78-76.22C111.64,100,92.35,64,74,64c-9.38,0-19.94,10-28.25,26.67A138.18,138.18,0,0,0,32,149.31c0,13.2,2.38,24.12,6.88,31.58S49.82,192,58.56,192c15.12,0,30.85-24.54,44.23-48.55a8,8,0,0,1,14,7.8C101.46,178.71,83.07,208,58.56,208c-14.41,0-26-6.52-33.39-18.85-6-10-9.17-23.73-9.17-39.84A154.81,154.81,0,0,1,31.42,83.54C42.82,60.62,57.94,48,74,48c27.94,0,47.77,37,68.78,76.22C159.79,156,179.08,192,197.44,192c8.74,0,15.18-3.63,19.68-11.11S224,162.51,224,149.31a138.18,138.18,0,0,0-13.74-58.64C202,74,191.39,64,182,64c-8.36,0-17.68,7.48-28.51,22.88a8,8,0,1,1-13.08-9.21c9-12.74,23-29.67,41.59-29.67,16.05,0,31.17,12.62,42.57,35.54A154.81,154.81,0,0,1,240,149.31Z"></path></svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-0.5 bg-red-500 rotate-45"></div>
                  </div>
                </div>
              </div>
              <p className="text-gray-300">No pixels</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="relative">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="#f97316" viewBox="0 0 256 256"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm48-56a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,160ZM80,108a12,12,0,1,1,12,12A12,12,0,0,1,80,108Zm96,0a12,12,0,1,1-12-12A12,12,0,0,1,176,108Z"></path></svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-0.5 bg-red-500 rotate-45"></div>
                  </div>
                </div>
              </div>
              <p className="text-gray-300">No guesswork</p>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-2xl p-8">
            <h3 className="font-semibold text-lg mb-4 text-orange-400">You&apos;ll see:</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-700 rounded-lg p-4 text-center">
                <div className="text-sm text-gray-400">SMS sent</div>
                <div className="text-xl font-bold">1,500</div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 text-center">
                <div className="text-sm text-gray-400">SMS cost</div>
                <div className="text-xl font-bold">$97</div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 text-center">
                <div className="text-sm text-gray-400">Orders</div>
                <div className="text-xl font-bold">47</div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 text-center">
                <div className="text-sm text-gray-400">Revenue</div>
                <div className="text-xl font-bold text-green-400">$4,800</div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 text-center">
                <div className="text-sm text-gray-400">Net return</div>
                <div className="text-xl font-bold text-green-400">49x</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 bg-orange-50">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/images/johnny-boys-pizza-logo.png" 
              alt="Johnny Boys Pizza" 
              className="h-20 w-auto"
            />
          </div>
          <blockquote className="text-2xl text-gray-800 mb-6 leading-relaxed">
            We sent 1,500 SMS for $97. Got 47 orders worth $4,800. That&apos;s a 49x return.
          </blockquote>
          <cite className="text-gray-600 not-italic">
            — Johnny Boys Pizza, Langwarrin
          </cite>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Connect DAZZA to your ordering or POS system
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Connect your existing systems so DAZZA can detect promo code usage, attribute revenue to campaigns, and measure ROI automatically.
          </p>
          <p className="text-gray-500">
            Once connected, DAZZA waits for real transactions — nothing else.
          </p>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How Australian businesses use DAZZA
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Promotions and limited-time offers',
              'Event and seasonal campaigns',
              'Win-back campaigns for inactive customers',
              'New product or service launches',
              'Location or time-based announcements',
            ].map((useCase, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center gap-3">
                <svg className="w-5 h-5 text-orange-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                </svg>
                <span className="text-gray-800">{useCase}</span>
              </div>
            ))}
          </div>
          
          <p className="text-center text-gray-500 mt-8">All measured. All traceable.</p>
        </div>
      </section>

      {/* Reassurance Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Built for business owners — not marketers
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            DAZZA is designed for businesses that want simple tools, clear numbers, no marketing jargon, no contracts, and no surprises.
          </p>
          <p className="text-lg font-semibold text-gray-800">
            If it can&apos;t be measured, it doesn&apos;t count.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-orange-500 to-amber-500">
        <div className="mx-auto max-w-4xl px-6 text-center text-white">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Send your first SMS today
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Create an account and get $5 free SMS credit. No card required to start.
          </p>
          <Link to="/sign-up" className="inline-block bg-white text-orange-600 font-bold text-lg px-10 py-4 rounded-lg hover:bg-gray-100 transition-colors">
            Get Started Free
          </Link>
          <p className="text-sm opacity-75 mt-4">
            Top up anytime. No commitment.
          </p>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600">
              Talk to us about how DAZZA can help your business grow
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            {contactSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Thanks for reaching out!</h3>
                <p className="text-gray-600">We'll give you a call at your preferred time.</p>
              </div>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); setContactSubmitted(true); }} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Business Name *</label>
                    <input type="text" required placeholder="Your Business Name" value={contactForm.businessName} onChange={(e) => setContactForm(f => ({ ...f, businessName: e.target.value }))} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Contact Name *</label>
                    <input type="text" required placeholder="Your Full Name" value={contactForm.contactName} onChange={(e) => setContactForm(f => ({ ...f, contactName: e.target.value }))} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Phone Number *</label>
                    <input type="tel" required placeholder="+61 4XX XXX XXX" value={contactForm.phone} onChange={(e) => setContactForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Preferred Time for Call</label>
                    <select value={contactForm.time} onChange={(e) => setContactForm(f => ({ ...f, time: e.target.value }))} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20 bg-white">
                      <option value="">Select a time</option>
                      <option value="morning">Morning (9am - 12pm)</option>
                      <option value="afternoon">Afternoon (12pm - 5pm)</option>
                      <option value="evening">Evening (5pm - 7pm)</option>
                    </select>
                  </div>
                </div>

                <div className="pt-4">
                  <button type="submit" className="btn-primary w-full md:w-auto text-lg px-8 py-4">
                    Submit Request
                  </button>
                </div>

                <div className="pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500 text-center">
                    Or email us directly at{' '}
                    <a href="mailto:contact@dazza.ai" className="text-orange-600 hover:text-orange-700 font-medium">
                      contact@dazza.ai
                    </a>
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="#ffffff" viewBox="0 0 256 256">
                <path d="M169.57,72.59A80,80,0,0,0,16,104v64a16,16,0,0,0,16,16H86.67A80.15,80.15,0,0,0,160,232h64a16,16,0,0,0,16-16V152A80,80,0,0,0,169.57,72.59ZM32,104a64,64,0,1,1,64,64H32ZM224,216H160a64.14,64.14,0,0,1-55.68-32.43A79.93,79.93,0,0,0,174.7,89.71,64,64,0,0,1,224,152Z"></path>
              </svg>
              <div>
                <div className="text-lg font-bold text-white">DAZZA</div>
                <div className="text-xs text-gray-400">dazza.ai</div>
              </div>
            </div>
            
            <div className="text-center md:text-left">
              <div className="text-sm text-gray-400 mb-1">Wallet-based SMS marketing</div>
              <div className="text-sm text-gray-400 mb-1">ROI measured by real transactions</div>
              <div className="text-sm text-gray-400">Australian owned and operated 🇦🇺</div>
            </div>
            
            <div className="flex items-center gap-6">
              <Link to="/sign-in" className="text-sm text-gray-400 hover:text-white transition-colors">Log in</Link>
              <Link to="/sign-up" className="text-sm text-gray-400 hover:text-white transition-colors">Sign up</Link>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-500">
            <div className="mb-2">© 2026 DAZZA. Built for Australian businesses.</div>
            <div className="text-xs text-gray-600 mb-2">
              <a href="https://iconscout.com/lottie-animations/kangaroo" className="underline" target="_blank" rel="noopener noreferrer">Kangaroo</a> by <a href="https://iconscout.com/contributors/google-inc" className="underline" target="_blank" rel="noopener noreferrer">Google Inc.</a> on <a href="https://iconscout.com/" className="underline" target="_blank" rel="noopener noreferrer">IconScout</a>
            </div>
            <div className="text-xs text-gray-600" data-testid="landing-build-version">
              v{BUILD_INFO.version} • Build #{BUILD_INFO.buildNumber}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
